import * as vp from 'vopay';
import { Button } from 'UI';
import * as Yup from 'yup';
import { FunctionComponent, useEffect, useState } from 'react';
import { Formik, FormikErrors, FormikHandlers, FormikHelpers, FormikTouched, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import styles from '../AccountingSystemOpen.module.scss';
import useFetchFinancialInstitutionBranches from '../../../../../hooks/useApi/useFetchFinancialInstitutionBranches';
import { buildPaymentUpdateRows, PaymentUpdateRows } from './buildPaymentUpdateRows.helper';
import { PaymentInfoAndUpdate } from '../types';
import { useClientData } from '../../../../../providers/ClientData.provider';
import ElinxConnect from '../../../../ElinxConnect/ElinxConnect';
import useElinxRows from '../Elinx/useElinxRows';
import ElinxInfoBanner from '../Elinx/ElinxInfoBanner';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import {
  getDefaultBankingPaymentRails,
  getDefaultPaymentSelectionMethods,
} from '../../../../Elinx/ElinxPages/buildElinxRows.helper';
import {
  getRequiredIcon,
  updateCustomerData,
  roundCurrency,
  seedElinxDefaults,
  isIQ11Enabled,
  isElinxActive,
  hasElinxPermission,
} from '../utils';
import { usePermissions } from '@/providers/Permissions.provider';
import useFetchRailzBillCreditNotes from '../../../../../hooks/useApi/useFetchRailzBillCreditNotes';
import useFetchRailzInvoiceCreditNotes from '../../../../../hooks/useApi/useFetchRailzInvoiceCreditNotes';
import useDataFetchIndicator from '../../../../../hooks/useDataFetchIndicator';

interface BankingUpdateDialogueProps {
  isVisible: boolean;
  type: 'Payable' | 'Receivable';
  originalValue: vp.TemporaryBillsOrInvoicesRails | undefined;
  financialInstitutions: {
    [key: string]: vp.FinancialInstitution;
  };
  close: () => void;
  onSave: (savedValues: vp.TemporaryBillsOrInvoicesRails) => void;
  BusinessName: string;
  ServiceName: string;
}
type FC = FunctionComponent<BankingUpdateDialogueProps>;

const {
  Modal,
  SubTitle,
  Box,
  ElementContainer,
  TextField,
  Select,
  GoogleAddressField,
  Radio,
  DatepickerSimple,
  MultiSelectChip,
  Checkbox,
  Icon,
} = vp;

type BankingUpdateFormEffectsProps = {
  setCurrentValues: (values: PaymentInfoAndUpdate) => void;
  paymentUpdateRows: ReturnType<typeof buildPaymentUpdateRows>;
  totalAmount: number;
  isVisible: boolean;
  elinxSeed: Record<string, unknown>;
  fetchFinancialInstitutionBranches: ReturnType<typeof useFetchFinancialInstitutionBranches>[1];
  getFinancialInstitutionIDByNumber: (
    financialInstitutionNumber: string,
  ) => vp.FinancialInstitution['FinancialInstitutionID'] | null;
};

function BankingUpdateFormEffects({
  setCurrentValues,
  paymentUpdateRows,
  totalAmount,
  isVisible,
  elinxSeed,
  fetchFinancialInstitutionBranches,
  getFinancialInstitutionIDByNumber,
}: BankingUpdateFormEffectsProps) {
  const { values, errors, setFieldValue, validateForm } = useFormikContext<PaymentInfoAndUpdate>();

  useEffect(() => {
    if (values.FinancialInstitutionNumber) {
      const FinancialInstitutionID = getFinancialInstitutionIDByNumber(values.FinancialInstitutionNumber);
      if (FinancialInstitutionID) {
        fetchFinancialInstitutionBranches({ FinancialInstitutionID: Number(FinancialInstitutionID) });
      }
    }
  }, [values.FinancialInstitutionNumber]);
  useEffect(() => {
    setCurrentValues({
      ...values,
    });
  }, [values]);
  useEffect(() => {
    validateForm();
  }, [values, errors, paymentUpdateRows]);
  useEffect(() => {
    setFieldValue('PaymentAmount', String(roundCurrency(totalAmount - values.CreditAmount)) || '');
  }, [values.PaymentDate, values.CreditNotes, isVisible]);
  useEffect(() => {
    if (isVisible) {
      setFieldValue('PaymentMethod', '');
      Object.entries(elinxSeed).forEach(([key, value]) => setFieldValue(key, value));
    } else {
      setFieldValue('CreditNotes', '');
      setFieldValue('CreditAmount', 0);
    }
    validateForm();
  }, [isVisible]);

  return null;
}

const BankingUpdateDialogue: FC = ({
  isVisible,
  type,
  close,
  onSave,
  financialInstitutions,
  originalValue,
  BusinessName,
  ServiceName,
}: BankingUpdateDialogueProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'AccountingSystem.OpenTab.BankingUpdateDialogue' });
  const { t: tPaymentForm } = useTranslation('translations', { keyPrefix: 'AccountingSystem.OpenTab.PaymentForm' });
  const { t: tElinx } = useTranslation('translations', { keyPrefix: 'AccountingSystem.OpenTab.Elinx' });
  const [creditNotes, setCreditNotes] = useState<vp.CreditNote[]>([]);
  const [, , fetchRailzBillsCreditNotes] = useFetchRailzBillCreditNotes({});
  const [, , fetchRailzInvoiceCreditNotes] = useFetchRailzInvoiceCreditNotes({});
  const defaultValues: PaymentInfoAndUpdate = {
    PaymentDate: Date.now().toString(),
    PaymentAmount: '0.00',
    PaymentMethod: '',
    InteracMethod: 'Email Address',
    CreditNotes: '',
    CreditAmount: 0,
    ...seedElinxDefaults(originalValue?.Customer),
  };
  const [currentValues, setCurrentValues] = useState<PaymentInfoAndUpdate>(defaultValues);
  const [showElinxConnect, setShowElinxConnect] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  useEffect(() => {
    const fetchData = async () => {
      if (isVisible) {
        setDataFetchingInfo(true, 'loading some data', 'fullScreen');
        if (type === 'Receivable') {
          if (originalValue?.customerRef?.id) {
            const creditsResponse = await fetchRailzInvoiceCreditNotes({
              BusinessName,
              ServiceName,
              CustomerRefID: originalValue?.customerRef?.id,
            });
            if (creditsResponse.Success && creditsResponse.CreditNotes) {
              setCreditNotes(creditsResponse.CreditNotes);
            } else {
              setCreditNotes([]);
            }
          }
        } else if (originalValue?.vendorRef?.id) {
          const creditsResponse = await fetchRailzBillsCreditNotes({
            BusinessName,
            ServiceName,
            VendorRefID: originalValue?.vendorRef?.id,
          });
          if (creditsResponse.Success && creditsResponse.CreditNotes) {
            setCreditNotes(creditsResponse.CreditNotes);
          } else {
            setCreditNotes([]);
          }
        }
        setDataFetchingInfo(false);
      } else {
        setCreditNotes([]);
      }
    };

    fetchData();
  }, [isVisible]);
  const { response } = useClientData();
  const { Country } = response;
  const iQ11Enabled = isIQ11Enabled(response);
  const { permissions } = usePermissions();
  const canUseElinx = hasElinxPermission(type, permissions, vp.toBool(response.PayLinkEnabled));
  const elinxRows = useElinxRows({ type, country: Country, iQ11Enabled });
  const { LockedSelectOptions } = useLockedFeatures();
  const elinxFormType: 'Pay' | 'Collect' = type === 'Receivable' ? 'Collect' : 'Pay';
  const elinxSeed = {
    ...seedElinxDefaults(originalValue?.Customer),
    ElinxPaymentSelectionMethod: getDefaultPaymentSelectionMethods(
      elinxFormType,
      Country,
      LockedSelectOptions,
      undefined,
      'single',
    ),
    ElinxBankingPaymentRail: getDefaultBankingPaymentRails(
      elinxFormType,
      Country,
      LockedSelectOptions,
      undefined,
      'single',
    ),
  };
  const [financialInstitutionBranchesResponse, fetchFinancialInstitutionBranches] =
    useFetchFinancialInstitutionBranches({});
  const [elinxConnectSettings, setElinxConnectSettings] = useState<vp.EmbedUrlRequest>({
    RedirectURL: 'https://vopay.com',
    Country,
    PaymentSelectionMethod: 'credit',
    ClientControlled: false,
    RedirectMethod: 'JavascriptMessage',
  });
  const totalAmount = Number(originalValue?.amountDue) || Number(originalValue?.totalAmount) || 0;
  const paymentUpdateRows = buildPaymentUpdateRows({
    type,
    country: Country,
    totalAmount,
    currency: originalValue?.currency || '',
    branchTransitNumbers: Object.values(financialInstitutionBranchesResponse.Branches || {}).map((value) => {
      const { BranchTransitNumber, Address, TownCity, PostalCode, Province } = value;
      return {
        label: `${BranchTransitNumber} ${Address}, ${TownCity}, ${PostalCode}, ${Province}`,
        value: BranchTransitNumber.toString(),
        renderOption: (
          <div>
            {' '}
            {BranchTransitNumber} <br /> {`${Address}, ${TownCity}, ${PostalCode}, ${Province}`}
          </div>
        ),
      };
    }),
    financialInstitutions: Object.values(financialInstitutions || {}).map((value) => ({
      label: `${value.FinancialInstitutionNumber} ${value.FIName}`,
      value: value.FinancialInstitutionNumber,
      renderOption: (
        <div>
          {value.FinancialInstitutionNumber} <br /> {value.FIName}
        </div>
      ),
    })),
    creditNotes,
    t: tPaymentForm,
    tElinx,
    elinxRows,
    canUseElinx,
  });
  const saveForm = (savedValues: PaymentInfoAndUpdate) => {
    if (originalValue) {
      onSave(
        updateCustomerData(
          savedValues,
          originalValue,
          Country,
          type,
          savedValues.PaymentAmount,
          new Date(Number(savedValues.PaymentDate)).toLocaleDateString('en-ca'),
          savedValues.CreditNotes,
        ),
      );
    }
  };
  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={(values, { resetForm }) => {
        saveForm(values);
        resetForm();
      }}
      validationSchema={Yup.object().shape(getValidationSchema(currentValues, paymentUpdateRows))}
      validateOnChange
      validateOnBlur
    >
      {({
        values,
        touched,
        errors,
        isValid,
        handleChange,
        handleBlur,
        handleSubmit,
        setValues,
        setTouched,
        setFieldValue,
        resetForm,
        submitForm,
        validateForm,
      }) => {
        return (
          <>
            <Modal isVisible={isVisible} enableBgClick={false}>
              <div className={[styles.dialogue, styles.dialogueContent].join(' ')}>
                <form onSubmit={handleSubmit} className={styles.dialogueForm}>
                  <BankingUpdateFormEffects
                    setCurrentValues={setCurrentValues}
                    paymentUpdateRows={paymentUpdateRows}
                    totalAmount={totalAmount}
                    isVisible={isVisible}
                    elinxSeed={elinxSeed}
                    fetchFinancialInstitutionBranches={fetchFinancialInstitutionBranches}
                    getFinancialInstitutionIDByNumber={getFinancialInstitutionIDByNumber}
                  />
                  <Icon
                    className={styles.closeButton}
                    icon="cross"
                    height="12px"
                    width="12px"
                    onClick={() => {
                      resetForm();
                      validateForm();
                      close();
                    }}
                  />

                  <Box base="8" gap="lg" flx="col">
                    <SubTitle
                      subTitle={
                        type === 'Receivable'
                          ? t('title_Receivable', { defaultValue: 'Collect Receivable' })
                          : t('title_Payable', { defaultValue: 'Send Payment' })
                      }
                      center
                    />

                    <Box width="100%" gapSize="24px" base="8" flx="col">
                      {paymentUpdateRows
                        .filter((element) => element.visible(values))
                        .map((element) =>
                          getFormElement(
                            element,
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            setValues,
                            setTouched,
                            setFieldValue,
                          ),
                        )}
                      {isElinxActive(values) && <ElinxInfoBanner type={type} />}
                    </Box>

                    {isElinxActive(values) && (
                      <div className={styles.dialogueTerm}>
                        {type === 'Receivable'
                          ? t('accountInfoSaved_Receivable', {
                              defaultValue: 'Account information is saved once the collection is sent.',
                            })
                          : t('accountInfoSaved_Payable', {
                              defaultValue: 'Account information is saved once the payment is sent.',
                            })}
                      </div>
                    )}

                    {values.PaymentMethod !== 'Credit Card' && values.PaymentMethod !== 'Debit Card' && (
                      <Button
                        id="save-and-collect"
                        type="submit"
                        colorScheme="primary"
                        disabled={!isValid}
                        style={{ width: '100%' }}
                      >
                        {type === 'Receivable'
                          ? t('collectButton', { defaultValue: 'Collect' })
                          : t('payButton', { defaultValue: 'Pay' })}
                      </Button>
                    )}
                    {values.PaymentMethod === 'Credit Card' && (
                      <Button
                        id="save-and-collect"
                        type="button"
                        colorScheme="primary"
                        disabled={!isValid}
                        style={{ width: '100%' }}
                        onClick={() => {
                          setElinxConnectSettings({
                            RedirectURL: 'https://vopay.com',
                            Country,
                            PaymentSelectionMethod: 'credit',
                            ClientControlled: false,
                            RedirectMethod: 'JavascriptMessage',
                          });
                          setShowElinxConnect(true);
                        }}
                      >
                        {t('provideCreditCardDetails', { defaultValue: 'Provide Credit Card Details' })}
                      </Button>
                    )}
                    {values.PaymentMethod === 'Debit Card' && (
                      <Button
                        id="save-and-collect"
                        type="button"
                        colorScheme="primary"
                        disabled={!isValid}
                        style={{ width: '100%' }}
                        onClick={() => {
                          setElinxConnectSettings({
                            RedirectURL: 'https://vopay.com',
                            Country,
                            PaymentSelectionMethod: 'debitcard',
                            ClientControlled: false,
                            RedirectMethod: 'JavascriptMessage',
                          });
                          setShowElinxConnect(true);
                        }}
                      >
                        {t('provideDebitCardDetails', { defaultValue: 'Provide Debit Card Details' })}
                      </Button>
                    )}
                  </Box>
                </form>
              </div>
            </Modal>
            {showElinxConnect && (
              <ElinxConnect
                setting={elinxConnectSettings}
                onSuccess={(eventValues) => {
                  if (values.PaymentMethod === 'Debit Card') {
                    setValues({ ...values, TokenDebit: eventValues.data.Token });
                  } else {
                    setValues({ ...values, TokenCredit: eventValues.data.Token });
                  }

                  void submitForm();
                  setShowElinxConnect(false);
                }}
                onClose={() => {
                  setShowElinxConnect(false);
                }}
              />
            )}
          </>
        );
      }}
    </Formik>
  );
  function getFormElement(
    element: PaymentUpdateRows,
    values: PaymentInfoAndUpdate,
    errors: FormikErrors<PaymentInfoAndUpdate>,
    touched: FormikTouched<PaymentInfoAndUpdate>,
    handleChange: FormikHandlers['handleChange'],
    handleBlur: FormikHandlers['handleBlur'],
    setValues: FormikHelpers<PaymentInfoAndUpdate>['setValues'],
    setTouched: FormikHelpers<PaymentInfoAndUpdate>['setTouched'],
    setFieldValue: FormikHelpers<PaymentInfoAndUpdate>['setFieldValue'],
  ) {
    const {
      type: elementType,
      id,
      preset,
      placeholder,
      label,
      subtitle,
      showValue,
      require: required,
      getSelectOptions,
      getRadioOptions,
      container,
      paymentRows,
      inputWidth,
      min,
      getLeftElement,
      getRightElement,
      disabled,
      disabledMessage,
      renderOption,
      filterable,
    } = element;
    if (element.type === 'elinx') {
      return element.render
        ? element.render({ values, errors, touched, handleChange, handleBlur, setFieldValue })
        : null;
    }
    if (container === 'inputs') {
      const isRequired = required(values);
      return (
        <Box flx="col" width="100%">
          <ElementContainer
            width="100%"
            padding="0 0 0"
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            isBold={false}
            input={
              <Box flx="col" width="100%">
                {paymentRows
                  .filter((subElement) => subElement.visible(values))
                  .map((subElement) =>
                    getFormElement(
                      subElement,
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      setValues,
                      setTouched,
                      setFieldValue,
                    ),
                  )}
              </Box>
            }
          />
        </Box>
      );
    }
    if (container === 'wrapper' || id === undefined) {
      return (
        <Box gap="sm" ai="fs" width="100%">
          {paymentRows
            .filter((subElement) => subElement.visible(values))
            .map((subElement) =>
              getFormElement(
                subElement,
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setValues,
                setTouched,
                setFieldValue,
              ),
            )}
        </Box>
      );
    }
    const isRequired = required(values);

    switch (elementType) {
      case 'input':
        return (
          <ElementContainer
            width="100%"
            padding="0 0 6px"
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            isBold={false}
            input={
              <Box flx="col" gapSize="6px">
                <TextField
                  variant="outlineShadow"
                  preset={preset}
                  name={id}
                  placeholder={placeholder}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  inputWidth={inputWidth}
                  disabled={disabled ? disabled(values) : false}
                  elementLeft={getLeftElement ? getLeftElement(values) : undefined}
                  elementRight={getRightElement ? getRightElement(values) : undefined}
                  width="100%"
                  value={String(values[id] || '')}
                  error={errors[id] && touched[id] ? errors[id]?.toString() : null}
                />
                {disabled && disabled(values) && disabledMessage && (
                  <div className={styles.disabledMessage}>{disabledMessage}</div>
                )}
              </Box>
            }
          />
        );
      case 'select':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            padding="0 0 6px"
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            input={
              <Select
                version="2"
                renderOption={renderOption}
                options={getSelectOptions ? getSelectOptions(values) : []}
                placeholder={placeholder || ''}
                name={id}
                onBlur={handleBlur}
                onChange={handleChange}
                filterable={filterable}
                renderSelectedValue={(value, { Placeholder }) => (
                  <div className={styles.dropDownValue}>
                    {showValue
                      ? value?.value || <Placeholder />
                      : value?.renderOption || value?.label || <Placeholder />}
                  </div>
                )}
                value={String(values[id] || '')}
                sort={false}
                errorMsg={errors[id] && touched[id] ? errors[id]?.toString() : undefined}
              />
            }
          />
        );
      case 'googleAddress':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            padding="0 0 6px"
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            input={
              <GoogleAddressField
                variant="outlineShadow"
                ISOCountry={['US']}
                preset={preset}
                placeholder=""
                name={id}
                onBlur={handleBlur}
                onChange={handleChange}
                value={String(values[id] || '')}
                error={errors[id] && touched[id] ? errors[id]?.toString() : null}
                onGoogleAddressSelect={(addressComponents: vp.AddressComponents) => {
                  handleChange(vp.createSyntheticEvent('Address', addressComponents.address));
                  handleChange(vp.createSyntheticEvent('City', addressComponents.city));
                  handleChange(vp.createSyntheticEvent('State', addressComponents.province));
                  handleChange(vp.createSyntheticEvent('ZipCode', addressComponents.postalcode));
                }}
              />
            }
          />
        );
      case 'radio':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            padding="0 0 6px"
            width="100%"
            isBold={false}
            justifyContent="flex-start"
            input={
              <Radio
                key={id}
                name={id}
                value={String(values[id])}
                options={getRadioOptions ? getRadioOptions(values) : {}}
                noOrdering
                onBlur={() => {}}
                onChange={handleChange}
                variant="inline"
                setCheckedValue={(value) => {
                  setValues({ ...values, Language: value as 'en' | 'fr' });
                }}
              />
            }
          />
        );

      case 'selectFinanacialInstitution':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            padding="0 0 6px"
            width="100%"
            isBold={false}
            justifyContent="flex-start"
            input={
              <Select
                version="2"
                options={getSelectOptions ? getSelectOptions(values) : []}
                placeholder={placeholder || ''}
                name={id}
                renderSelectedValue={(value, { Placeholder }) => (
                  <div className={styles.dropDownValue}>
                    {showValue
                      ? value?.value || <Placeholder />
                      : value?.renderOption || value?.label || <Placeholder />}
                  </div>
                )}
                renderOption={renderOption}
                onBlur={handleBlur}
                onChange={(e) => {
                  setValues({ ...values, BankTransitNumber: '' });
                  setTouched({ ...touched, BankTransitNumber: false });
                  handleChange(e);
                }}
                filterable={filterable}
                value={String(values[id] || '')}
                sort={false}
                errorMsg={errors[id] && touched[id] ? errors[id]?.toString() : undefined}
              />
            }
          />
        );
      case 'multiSelect':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            padding="0 0 6px"
            input={
              <MultiSelectChip
                name={id}
                placeholder={placeholder || ''}
                options={getSelectOptions ? getSelectOptions(values) : []}
                optionValue={(option) => option.value}
                disableOptionWhen={(option) => !!option.isDisabled}
                value={String(values[id] || '')
                  .split(',')
                  .filter(Boolean)}
                disabled={disabled ? disabled(values) : false}
                sort={false}
                onChange={(e) => {
                  setFieldValue(id, e.target.value);
                  const selectedIds = e.target.value.split(',').filter(Boolean);
                  setFieldValue(
                    'CreditAmount',
                    creditNotes
                      .filter((item) => selectedIds.includes(item.id))
                      .reduce((acc, item) => roundCurrency(acc + item.remainingCredit), 0),
                  );
                }}
                onDelete={(deleted) => {
                  const selectedIds = String(values[id] || '')
                    .split(',')
                    .filter(Boolean)
                    .filter((v) => v !== deleted);
                  setFieldValue(id, selectedIds.join(','));
                  setFieldValue(
                    'CreditAmount',
                    creditNotes
                      .filter((item) => selectedIds.includes(item.id))
                      .reduce((acc, item) => roundCurrency(acc + item.remainingCredit), 0),
                  );
                }}
                errorMsg={errors[id] && touched[id] ? errors[id]?.toString() : undefined}
              />
            }
          />
        );

      case 'date':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            isBold={false}
            padding="0 0 6px"
            justifyContent="flex-start"
            input={
              <DatepickerSimple
                variant="outlineShadow"
                name={id}
                value={String(values[id] || '')}
                onBlur={handleBlur}
                onChange={handleChange}
                error={errors[id] ? errors[id]?.toString() : null}
                min={vp.createTimestamp(min)}
              />
            }
          />
        );
      case 'checkbox':
        return (
          <div className={styles.elinxToggle}>
            <Checkbox
              name={id}
              value={!values[id]}
              disabled={disabled ? disabled(values) : false}
              onBlur={() => {}}
              onChange={() => setFieldValue(id, !values[id])}
              size="sm"
              variant="rounded"
              hasNoPadding
            />
            <div className={styles.elinxToggleText}>
              <span className={styles.elinxToggleLabel}>{label}</span>
              {subtitle && <span className={styles.elinxToggleSubtitle}>{subtitle}</span>}
            </div>
          </div>
        );
      default:
        throw new Error(`Invalid payment type: ${type}`);
    }
  }
  function getValidationSchema(
    ctx: PaymentInfoAndUpdate,
    rows: PaymentUpdateRows[],
    initialValidationSchema?: { [key: string]: any },
  ): { [key: string]: any } {
    const validations = rows.reduce(
      (existingFields, row) =>
        row.id && row.container === 'field'
          ? {
              ...existingFields,
              [row.id]: row.validate ? row.validate(ctx) : undefined,
            }
          : { ...existingFields, ...getValidationSchema(ctx, row.paymentRows) },
      { ...initialValidationSchema },
    );

    return validations;
  }

  function getFinancialInstitutionIDByNumber(number: string) {
    for (const key in financialInstitutions) {
      if (financialInstitutions[key].FinancialInstitutionNumber.toString() === number) {
        return financialInstitutions[key].FinancialInstitutionID;
      }
    }
    return null; // Return null if the number is not found
  }
};

export default BankingUpdateDialogue;
